import React, { useEffect, useRef, useState } from "react";
import { TrackingClient } from "@sparefoot/tracking-client";
import PropTypes from "prop-types";
import { traceError } from "utils/traceContext";

const YoutubePlayer = ({ src, onError, title }) => {
	const playerRef = useRef(null);
	const containerRef = useRef(null);
	const scriptRef = useRef(null);
	const intervalRef = useRef(null);
	const isTrackingSent = useRef(false);
	const videoNameRef = useRef("");
	const durationRef = useRef(0);

	const [durationPlayed, setPlayedDuration] = useState(0);

	const YT_ERROR_MESSAGES = {
		2: "Invalid parameter",
		5: "HTML5 player error",
		100: "Video not found",
		101: "Video not playable in embedded players",
		150: "Video not playable in embedded players"
	};

	const onStateChange = (event) => {
		if (!globalThis.YT?.PlayerState) return;
		if (
			event.data === globalThis.YT.PlayerState.PLAYING &&
			!isTrackingSent.current
		) {
			intervalRef.current = setInterval(() => {
				if (playerRef.current?.getCurrentTime) {
					const currentTime = playerRef.current.getCurrentTime();
					setPlayedDuration(currentTime);
				}
			}, 1000);
		} else {
			clearInterval(intervalRef.current);
		}
	};

	const onYouTubeIframeAPIReady = () => {
		if (!globalThis.YT) return;
		if (!containerRef.current) return;
		try {
			playerRef.current = new globalThis.YT.Player(containerRef.current, {
				events: {
					onReady: (event) => {
						durationRef.current = event.target.getDuration();
						videoNameRef.current =
							event.target.getVideoData().title;
					},
					onStateChange: (event) => onStateChange(event),
					onError: (event) => {
						const message =
							YT_ERROR_MESSAGES[event.data] ||
							"Unknown error occurred";
						onError(message);
					}
				}
			});
		} catch (e) {
			traceError("YoutubePlayer: Error initializing YT Player", e);
			onError(e.message);
		}
	};

	const trackVideo = () => {
		// Only track if we have valid video metadata (onReady has completed)
		if (
			!isTrackingSent.current &&
			videoNameRef.current &&
			durationRef.current > 0
		) {
			TrackingClient.trackVideoWatched({
				video_name: videoNameRef.current,
				video_duration: durationRef.current,
				video_duration_played: Math.ceil(durationPlayed)
			});
			isTrackingSent.current = true;
		}
	};

	useEffect(() => {
		let isCanceled = false;
		let checkInterval = null;

		const init = () => {
			if (isCanceled) return;
			onYouTubeIframeAPIReady();
		};

		if (globalThis.YT) {
			try {
				if (playerRef.current) {
					playerRef.current?.destroy();
				}
			} catch (e) {
				traceError(
					"YoutubePlayer: Error destroying existing player",
					e
				);
			}
			init();
		} else {
			const tag = document.createElement("script");
			tag.src = "https://www.youtube.com/iframe_api";
			scriptRef.current = tag;
			const firstScriptTag = document.getElementsByTagName("script")[0];
			firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
			checkInterval = setInterval(() => {
				if (globalThis.YT?.Player) {
					clearInterval(checkInterval);
					init();
				}
			}, 100);
		}

		return () => {
			isCanceled = true;
			isTrackingSent.current = false;
			if (checkInterval) {
				clearInterval(checkInterval);
			}
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
			}
			try {
				if (playerRef.current) {
					playerRef.current?.destroy();
				}
			} catch (e) {
				traceError("YoutubePlayer: Error cleaning up YT Iframe API", e);
			}
		};
	}, [src]);

	useEffect(() => {
		if (durationPlayed > 10 && !isTrackingSent.current) {
			trackVideo();
			clearInterval(intervalRef.current);
		}
	}, [durationPlayed]);

	return (
		<div className="video-wrapper">
			<iframe
				data-testid="youtube-player-iframe"
				ref={containerRef}
				title={title || "YouTube Video Player"}
				width="100%"
				height="100%"
				src={`https://www.youtube.com/embed/${src}?enablejsapi=1&playsinline=1`}
				frameBorder="0"
				allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
				allowFullScreen
			/>
		</div>
	);
};

export default YoutubePlayer;

YoutubePlayer.propTypes = {
	src: PropTypes.string.isRequired,
	onError: PropTypes.func.isRequired,
	title: PropTypes.string.isRequired
};
